import type {
  ActivitySummary,
  StudentStatistics,
  UserKpis,
} from '@didaskill/domain/reporting/types';
import type { Session } from '@didaskill/domain/sessions/types';
import type { USER_VIEWS } from '@didaskill/domain/users/constants';
import type {
  GetStudentSummaryActivityQuery,
  GetStudentsQuery,
  GetUserSessionsQuery,
  PostStudentsExtractBody,
  StudentAcademicTranscript,
  StudentViewFull,
  StudentViewMinimal,
  UpdateStudentBody,
} from '@didaskill/domain/users/types';
import type { TypedResponse } from '@didaskill/types/misc';

import { HttpRepository } from '../httpRepository';

const e = encodeURIComponent;

export class StudentsRepository extends HttpRepository {
  async getStudents(
    query?: Omit<GetStudentsQuery, 'view'> & {
      view: USER_VIEWS.FULL | USER_VIEWS.MIN;
    },
  ): Promise<{
    count: number;
    users: StudentViewFull[] | StudentViewMinimal[];
  }> {
    const {
      body: { count, users },
    } = await this.http.get('/api/v1/users/students/').query(query);

    return {
      count,
      users,
    };
  }

  /**
   * Update a given student.
   * @param userId - Student to update
   * @param params - Params.
   * @returns - The updated student.
   */
  async updateStudent(userId: string, params: UpdateStudentBody): Promise<StudentViewFull> {
    const { body: user } = await this.http
      .patch(`/api/v1/users/students/${e(userId)}`)
      .send(params);

    return user;
  }

  /**
   * Delete a profile picture from a student.
   *
   * @param userId - Student id.
   * @param pictureId - Picture id.
   * @returns Updated user.
   */
  async deleteProfilePicture(userId: string, pictureId: string): Promise<StudentViewFull> {
    const { body: updatedStudent } = await this.http
      .delete(`/api/v1/users/students/${e(userId)}/profile-pictures/${pictureId}`)
      .send();

    return updatedStudent;
  }

  /**
   * Fetch the student's academic transcript for a course.
   *
   * @param studentId - Id of the student.
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @returns Transcript data.
   */
  async getStudentAcademicTranscript(
    studentId: string,
    courseId: string,
    classId: string,
  ): Promise<StudentAcademicTranscript> {
    const { body: transcript } = await this.http
      .get(`/api/v1/users/students/${e(studentId)}/academic-transcript`)
      .query({
        courseId,
        classId,
      });

    return transcript;
  }

  /**
   * Fetch the student's academic transcript document for a course.
   *
   * @param studentId - Id of the student.
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @returns Transcript data.
   */
  async getStudentAcademicTranscriptDocument(
    studentId: string,
    courseId: string,
    classId: string,
  ): Promise<Blob> {
    const { body: transcript } = await this.http
      .get(`/api/v1/users/students/${e(studentId)}/academic-transcript-document`)
      .query({
        courseId,
        classId,
      })
      .responseType('blob');

    return transcript;
  }

  /**
   * Fetch the school certificate blob.
   *
   * @param studentId - Id of the student.
   * @param courseId - Course Id.
   * @param classId - Class id.
   * @returns The blob of the pdf file.
   */
  async getSchoolCertificate(studentId: string, courseId: string, classId: string): Promise<Blob> {
    const { body } = await this.http
      .get(`/api/v1/users/students/${e(studentId)}/school-certificate`)
      .query({ courseId, classId })
      .responseType('blob');

    return body;
  }

  /**
   * Fetch the student's KPIs for a course.
   *
   * @param studentId - Id of the student.
   * @param courseId - Id of the course.
   * @param classId - Id of the class.
   * @returns KPIs data.
   */
  async getStudentKpis(studentId: string, courseId: string, classId: string): Promise<UserKpis> {
    const { body } = await this.http.get(`/api/v1/users/students/${e(studentId)}/kpis`).query({
      courseId,
      classId,
    });

    return body;
  }

  /**
   * Fetch the student's sessions.
   * @param studentId - Id of the student.
   * @param query - Query params.
   * @returns - Paginated sorted list of sessions.
   */
  async getStudentSessions(studentId: string, query: GetUserSessionsQuery): Promise<Session[]> {
    const { body } = await this.http
      .get(`/api/v1/users/students/${e(studentId)}/sessions`)
      .query(query);

    return body;
  }

  /**
   * Impersonate a student.
   * @param studentId - Id of the student.
   * @returns Impersonation token.
   */
  async impersonateStudent(studentId: string): Promise<{ token: string }> {
    const { body } = await this.http.post(`/api/v1/impersonate`).send({
      userId: studentId,
    });

    return body as { token: string };
  }

  /**
   * Get the student statistics.
   * @param studentId - Student id.
   * @param courseId - Course id.
   * @returns The student statistics.
   */
  async getUserStatistics(
    studentId: string,
    query: {
      classId: string;
    },
  ): Promise<StudentStatistics> {
    const { body } = await this.http
      .get(`/api/v1/users/students/${e(studentId)}/statistics`)
      .query(query);

    return body;
  }

  /**
   * Get the student activity summary.
   * @param studentId - Student id.
   * @param courseId - Course id.
   * @returns Superagent response with student activity summary body.
   */
  async getStudentActivitySummary(
    studentId: string,
    query: GetStudentSummaryActivityQuery,
  ): Promise<TypedResponse<ActivitySummary>> {
    const res = await this.http
      .get(`/api/v1/users/students/${e(studentId)}/activity-summary`)
      .query(query);

    return res;
  }

  /**
   * Post students extract.
   * @param type- Type of extract, either 'ids' or 'all'.
   * @param studentIds - Array of student IDs to extract (required if type is 'ids').
   * @param excludedStudentIds - Array of student IDs to exclude from extract (optional if type is 'all').
   * @param documentTypes - Array of document types to extract.
   * @param filters - Optional filters to apply to filter student courses.
   * @returns 202 status if operation is accepted.
   */
  async postStudentsExtract({
    type,
    studentIds,
    excludedStudentIds,
    filters,
    documentTypes,
  }: PostStudentsExtractBody): Promise<number> {
    const res = await this.http.post('/api/v1/users/students/extract').send({
      type,
      studentIds,
      excludedStudentIds,
      filters,
      documentTypes,
    });

    return res.status;
  }
}
